export interface ClientChartAddressInput {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  serviceAddress?: string | null;
  homeAddress?: string | null;
}

function trim(value: unknown): string {
  return String(value || '').trim();
}

/** One-line display from saved street, city, state, and ZIP. Does not read free text. */
export function formatSavedMemberAddress(input: ClientChartAddressInput): string {
  const street = trim(input.street);
  const city = trim(input.city);
  const state = trim(input.state).toUpperCase();
  const zip = trim(input.zip);
  const locality = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, locality].filter(Boolean).join(', ');
}

function sameAddressText(left: string, right: string): boolean {
  const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalize(left) !== '' && normalize(left) === normalize(right);
}

/**
 * Saved treatment-location text stays visible when it is the only address on file
 * or it differs from the member address. It is not copied into the member address.
 */
export function legacyServiceAddressForReview(input: ClientChartAddressInput): string {
  const saved = trim(input.serviceAddress);
  if (!saved) return '';
  const formatted = formatSavedMemberAddress(input);
  if (formatted && sameAddressText(saved, formatted)) return '';
  return saved;
}

export function memberAddressFormError(input: ClientChartAddressInput): string | null {
  const street = trim(input.street);
  const city = trim(input.city);
  const state = trim(input.state).toUpperCase();
  const zip = trim(input.zip);
  if (state && !/^[A-Z]{2}$/.test(state)) {
    return 'State must be a 2-letter code, such as AZ.';
  }
  if (zip && !/^\d{5}(?:-\d{4})?$/.test(zip)) {
    return 'ZIP must be 5 digits or ZIP+4 (#####-####).';
  }
  const started = Boolean(street || city || state);
  if (started && (!street || !city || !state || !zip)) {
    return 'Enter the street address, city, state, and ZIP together. Leave all of them blank if you are not ready to save this address.';
  }
  return null;
}

/** Save body for the edit form. Treatment location and home address are not replaced by the member address. */
export function clientChartAddressSave(input: ClientChartAddressInput): {
  error: string | null;
  serviceAddress: string;
  homeAddress: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  formattedMemberAddress: string;
} {
  const street = trim(input.street);
  const city = trim(input.city);
  const state = trim(input.state).toUpperCase();
  const zip = trim(input.zip);
  return {
    error: memberAddressFormError(input),
    serviceAddress: trim(input.serviceAddress),
    homeAddress: trim(input.homeAddress),
    street,
    city,
    state,
    zip,
    formattedMemberAddress: formatSavedMemberAddress({ street, city, state, zip })
  };
}
